/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";

import { Breadcrumbs } from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useParams, useHistory } from "react-router-dom";
import * as yup from "yup";

import SettingsUserForm from "../../../../../../../components/Generic/SettingsUserForm";
import LoadingModal from "../../../../../../../components/LoadingModal";
import { useAppState } from "../../../../../../../context/StateContext";
import { UserProvider } from "../../../../../../../context/UserContext";
import useActionAutomaticSequence from "../../../../../../../hooks/useActionAutomaticSequence";
import useUserContext from "../../../../../../../hooks/useUserContext";
import { Timezone } from "../../../../../../../models/timezone";
import { User } from "../../../../../../../models/user";
import { AutomaticSequenceOptions, Status, UserTypes } from "../../../../../../../util/enums";
import { handleValidation } from "../../../../../../../util/formValidation";
import { userScheme } from "../../../../../../../util/genericSchemas";
import { validateDate, validateDateOfBirth } from "../../../../../../../util/validateBirthdate";
import FooterActions from "./FooterActions";
import useStyles from "./styles";

const UserMaintenance = () => {
  const classes = useStyles();
  const history = useHistory<{
    view?: boolean | undefined;
    tab?: string;
    tabPeople?: string;
  }>();

  const { t: translate } = useTranslation();
  const { userId } = useParams<{ userId?: string | undefined }>();
  const {
    rest: { user },
    timezones,
    isUpdating,
    isCreating,
    fetchingUser,
    handleUpdate,
    handleCreate,
    maintenance: {
      sequence,
      onSendResetPassword,
      loadingResetPassword,
      handleGoBackToPeopleTab,
      setSequence,
      errorResponse,
    },
  } = useUserContext(history);

  const { currentUser } = useAppState();

  const { getAutomaticSequenceByType } = useActionAutomaticSequence();

  const [viewOption, setViewOption] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<User>({} as User);

  getAutomaticSequenceByType(AutomaticSequenceOptions.USER, !userId, setSequence);

  const { genericObject: schemaObject } = userScheme(translate, newUser, currentUser);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    lastName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    isDateOfBirth: yup.boolean(),
    dateOfBirth: yup
      .string()
      .when("isDateOfBirth", {
        is: true,
        then: yup
          .string()
          .test("wrong-year", translate("theDateIsWrong"), (dateOfBirth) =>
            dateOfBirth ? validateDate(dateOfBirth) : false
          )
          .test(
            "date-of-birth",
            translate("theUserMustBeAtLeast18YearsOldAndUnder100YearsOld"),
            (dateOfBirth) => (dateOfBirth ? validateDateOfBirth(dateOfBirth) : false)
          ),
      })
      .nullable(),
    phone: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .test({
        name: "validate-phone",
        message: translate("pleaseEnterAValidPhoneNumber"),
        test: (value) => {
          return value ? isValidPhoneNumber(value || "") : true;
        },
      }),
    email: yup
      .string()
      .email()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    username: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    typeUser: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup.string().when("isProfileMoving", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    timezoneId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    roleId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    isFM: yup.boolean(),
    projects: yup.array().when("isFM", {
      is: true,
      then: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number().typeError(translate("thisFieldIsRequired")),
            name: yup.string().typeError(translate("thisFieldIsRequired")),
          })
        )
        .min(1, translate("thisUserNeedsToHaveSomeProjectSelected"))
        .required(translate("thisUserNeedsToHaveSomeProjectSelected")),
    }),
    isProfileMoving: yup.boolean(),
    branches: yup.array().when("isProfileMoving", {
      is: true,
      then: yup
        .array()
        .min(1, translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
    }),
  });

  const handleSubmit = () => {
    const validateSyncObject = schemaObject(newUser);

    if (
      handleValidation({
        schema,
        validateSyncObject,
        updateErrors: setErrors,
      })
    ) {
      if (userId) {
        handleUpdate(newUser);
      } else {
        handleCreate(newUser);
      }
    }
  };

  const getUserCode = (): string => {
    return `${sequence?.prefix ? sequence?.prefix : ""}${
      sequence?.nextValue ? sequence?.nextValue : ""
    }`;
  };

  const setDefaultValues = useCallback(() => {
    const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let timezoneFound: Timezone | undefined;

    if (user?.timezoneId)
      timezoneFound = timezones?.find((timezone) => timezone.id === user.timezoneId);
    else timezoneFound = timezones?.find((timezone) => timezone.name === currentTimezone);

    if (timezoneFound) {
      setNewUser((state) => ({
        ...(state || undefined),
        timezoneId: timezoneFound?.id,
        status: user?.status || Status.ACTIVE,
        typeUser: user?.typeUser || UserTypes.USER,
        code: user?.code || getUserCode(),
      }));
    }
  }, [timezones]);

  useEffect(() => {
    if (user) setNewUser(user);
  }, [user]);

  useEffect(() => {
    if (timezones.length > 0) setDefaultValues();
  }, [timezones]);

  useEffect(() => {
    if (history) {
      const { location } = history;

      if (location && location.state) {
        setViewOption(Boolean(location.state.view));
      }
    }
  }, [history]);

  useEffect(() => {
    if (newUser && hasFormBeenSubmitted) {
      const validateSyncObject = schemaObject(newUser);

      handleValidation({
        schema,
        validateSyncObject,
        updateErrors: (err) => setErrors(err),
      });
    }
  }, [newUser, hasFormBeenSubmitted]);

  return (
    <div className={classes.userMaintainerWrapper}>
      <header className={classes.userMaintainerHeader}>
        <Breadcrumbs
          separator={<NavigateNext />}
          aria-label="breadcrumb"
          className={classes.userMaintainerHeaderBreadcrumb}
        >
          <span
            data-cy="linkToGoBackToPeopleTab"
            className={`${classes.userMaintainerHeaderBreadcrumbTitle}`}
          >
            {translate("people")}
          </span>
          <span
            data-cy="linkToGoBackToPeopleTab"
            className={`${classes.userMaintainerHeaderBreadcrumbTitle} linkToPeopleTab`}
            onClick={() => handleGoBackToPeopleTab()}
          >
            {translate("users")}
          </span>
          <span
            data-cy="titleMaintainerHeader"
            className={`${classes.userMaintainerHeaderBreadcrumbTitle} active`}
          >
            {userId
              ? viewOption
                ? translate("viewUser")
                : translate("editUser")
              : translate("createUser")}
          </span>
        </Breadcrumbs>
      </header>
      <div className={classes.userMaintainerBody}>
        <SettingsUserForm
          view={viewOption}
          userInformation={newUser}
          errors={errors}
          sequence={sequence}
          isLoading={fetchingUser}
          updateOrCreate={(modifiedUser: User) => {
            setNewUser({ ...modifiedUser });
          }}
          errorResponse={errorResponse}
        />
      </div>
      <FooterActions
        user={
          user && {
            id: user.id,
            status: user.status,
            email: user?.email || "",
          }
        }
        loading={isCreating || isUpdating}
        loadingActions={loadingResetPassword}
        isOnViewMode={viewOption}
        setIsViewMode={() => {
          setViewOption((state) => !state);
        }}
        onSubmitForm={() => {
          if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
          handleSubmit();
        }}
        toggleUserStatus={(status: string) => handleUpdate({ ...newUser, status })}
        onSendResetPassword={(email) => {
          onSendResetPassword({ email });
        }}
        goBackToPeopleTab={() => handleGoBackToPeopleTab()}
      />
      <LoadingModal showing={isCreating || isUpdating} />
    </div>
  );
};

export default function Maintenance() {
  return (
    <UserProvider>
      <UserMaintenance />
    </UserProvider>
  );
}
